/**
 * ZeitkontoPage — Team-Übersicht aller Zeitkonten + Detail-Drawer pro MA.
 *
 * v2.242.0: Export-Dropdown (Excel/PDF), Stempel-Korrekturen mit Audit,
 *   Department-Filter, kumulierter Jahressaldo, Überstunden-Warnungen,
 *   Layout-Fixes gemäß SKILL-design.md.
 * v2.257.0: Inline-Edit für Kommt/Geht in Tagesübersicht, Layout-Padding,
 *   Rollen-Bug fix (admin → isAdmin), Korrektur-Badge pro Tag.
 *
 * @page /zeitkonto
 * @roles administrator, admin, ops_manager, teamleiter
 * @since v2.200.0
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X, Lock,
  AlertTriangle, Clock, ArrowRight, Download, Pencil,
  FileSpreadsheet, FileText, Check, Search, Users, TrendingUp
} from 'lucide-react';
import ModulEinstellungen from '../components/ModulEinstellungen';
import ManuelleEingabeOverlay from '../components/zeitkonto/ManuelleEingabeOverlay';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import MitarbeiterListItem from '../components/MitarbeiterListItem';

const api = (path, opts) => fetch(path, { credentials: 'include', ...opts }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
const apiPost = (path, body) => api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

const fmtStichtag = (iso) => iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '';
const fmtH = (v) => { const n = parseFloat(v) || 0; return n.toFixed(1).replace('.', ','); };
const fmtDelta = (v) => { const n = parseFloat(v) || 0; return (n >= 0 ? '+' : '') + n.toFixed(1).replace('.', ','); };
const fmtMinToH = (m) => { const n = parseFloat(m) || 0; const h = Math.floor(Math.abs(n) / 60); const min = Math.round(Math.abs(n) % 60); return `${n < 0 ? '-' : ''}${h}:${String(min).padStart(2, '0')}`; };
const fmtDate = (s) => { if (!s) return '—'; const d = new Date(s); return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`; };
const dayName = (s, locale) => { const d = new Date(s + 'T00:00:00'); return d.toLocaleDateString(locale === 'de' ? 'de-AT' : 'en-US', { weekday: 'short' }); };
const fmtTime = (ts) => { if (!ts) return ''; return new Date(ts).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Vienna' }); };

// ─── KPI Card ───
function Kpi({ label, value, sub, warning, icon: Icon, iconColor }) {
  return (
    <div className="min-w-0 border border-[var(--border-default,#E5E7EB)] rounded-xl p-5 bg-[var(--bg-card,#fff)] overflow-hidden">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={16} className={iconColor || 'text-[#338edc]'} />}
        <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] truncate">{label}</p>
      </div>
      <p className="text-2xl font-bold text-[var(--text-primary)] truncate">{value}</p>
      <div className="flex items-center gap-2 mt-1 min-w-0">
        {sub && <p className="text-sm text-[var(--text-muted)] truncate">{sub}</p>}
        {warning && (
          <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
            <AlertTriangle size={10} className="inline mr-0.5" />{warning}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Sort Header ───
function SortHeader({ col, children, sort, setSort, align = 'left' }) {
  const active = sort.col === col;
  const next = active && sort.dir === 'asc' ? 'desc' : 'asc';
  return (
    <th
      className={`text-${align} text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3 cursor-pointer select-none hover:text-[var(--text-primary)] transition-colors`}
      onClick={() => setSort({ col, dir: active ? next : 'asc' })}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {active && (sort.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
      </span>
    </th>
  );
}

// ─── Export Dropdown ───
function ExportDropdown({ monat, maId, t }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const monatParam = monat.substring(0, 7);
  const maParam = maId ? `&mitarbeiter_id=${maId}` : '';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-secondary)] hover:border-[#338edc] hover:text-[#338edc] transition-colors"
      >
        <Download size={14} />
        {t('zeitkonto.export', 'Export')}
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg py-1 min-w-[180px]">
          <a
            href={`/api/zeitkonto/export/excel?monat=${monatParam}${maParam}`}
            className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
            onClick={() => setOpen(false)}
          >
            <FileSpreadsheet size={14} className="text-emerald-600" />
            {t('zeitkonto.exportExcel', 'Excel (.xlsx)')}
          </a>
          <a
            href={`/api/zeitkonto/export/pdf?monat=${monatParam}${maParam}`}
            className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
            onClick={() => setOpen(false)}
          >
            <FileText size={14} className="text-red-500" />
            {t('zeitkonto.exportPdf', 'PDF (.pdf)')}
          </a>
        </div>
      )}
    </div>
  );
}

// ─── Detail Panel ───
function DetailPanel({ maId, monat, onClose, onDataChanged, t, locale, isAdmin }) {
  const [zk, setZk] = useState(null);
  // #1864 F5: Kopf-Werte aus persistierten Tageszeilen bis gestern (Server, eine Quelle)
  const [anzeige, setAnzeige] = useState(null);
  const [tage, setTage] = useState([]);
  const [zaAntraege, setZaAntraege] = useState([]);
  // Overlay state for manuelle Tageseingabe (#816, v2.355.0)
  const [overlayDay, setOverlayDay] = useState(null); // { datum, bestehendeDaten, hatBestehendeStempel }
  const [saveToast, setSaveToast] = useState(null);
  const [history, setHistory] = useState([]);
  const [maInfo, setMaInfo] = useState(null);
  const [feiertage, setFeiertage] = useState({});
  const [loading, setLoading] = useState(true);
  const [korrekturen, setKorrekturen] = useState([]); // all corrections this month
  const [inaktiveWochentage, setInaktiveWochentage] = useState([]); // #871: DB 0=Mo..6=So
  const [jahressaldo, setJahressaldo] = useState(null);
  const [mehrUeber, setMehrUeber] = useState(null); // #890: Mehr/Über-Summary
  const [auszahlOpen, setAuszahlOpen] = useState(false); // #1558: Überstunden-Auszahlung-Dialog
  const [kontoauszug, setKontoauszug] = useState(null); // #1776: ZA-Konto-Auszug des Monats
  const [expandedDays, setExpandedDays] = useState(() => new Set()); // #819: expanded session details
  // #869: Monats-Navigation innerhalb des Overlays. Start = übergebener Monat,
  // reset bei MA-Wechsel.
  const [viewMonat, setViewMonat] = useState(monat);
  useEffect(() => { setViewMonat(monat); }, [maId, monat]);
  const toggleExpand = (iso) => setExpandedDays(prev => {
    const next = new Set(prev);
    if (next.has(iso)) next.delete(iso); else next.add(iso);
    return next;
  });

  const load = useCallback(() => {
    if (!maId) return;
    setLoading(true);
    Promise.all([
      api(`/api/zeitkonto/${maId}/${viewMonat}`),
      api(`/api/zeitkonto/${maId}`),
      api(`/api/zeitkonto/${maId}/jahressaldo?jahr=${viewMonat.substring(0, 4)}`).catch(() => null),
      api(`/api/zeitkonto/${maId}/mehr-ueber-summary`).catch(() => null),
    ]).then(([detail, hist, js, mu]) => {
      setZk(detail.zeitkonto);
      setAnzeige(detail.anzeige || null);
      setTage(detail.tage || []);
      setZaAntraege(detail.za_antraege || []);
      setMaInfo(detail.mitarbeiter || null);
      setFeiertage(detail.feiertage || {});
      setKorrekturen(detail.korrekturen || []);
      setInaktiveWochentage(detail.inaktive_wochentage || []);
      setKontoauszug(detail.kontoauszug || null); // #1776
      setHistory((hist.data || []).slice(0, 6));
      setJahressaldo(js);
      setMehrUeber(mu);
    }).finally(() => setLoading(false));
  }, [maId, viewMonat]);

  useEffect(() => { load(); }, [load]);

  // Group korrekturen by datum for display under each day
  const korrekturenByDay = useMemo(() => {
    const map = {};
    for (const k of korrekturen) {
      const d = k.datum instanceof Date ? k.datum.toISOString().slice(0, 10) : String(k.datum).slice(0, 10);
      if (!map[d]) map[d] = [];
      map[d].push(k);
    }
    return map;
  }, [korrekturen]);

  const handleOverlaySaved = () => {
    setOverlayDay(null);
    setSaveToast(t('zeitkonto.savedToast', 'Arbeitszeit gespeichert'));
    setTimeout(() => setSaveToast(null), 3000);
    load();
    onDataChanged?.();
  };

  const handleAuszahlSaved = () => {
    setAuszahlOpen(false);
    setSaveToast(t('zeitkonto.auszahlSavedToast', 'Überstunden-Auszahlung gespeichert'));
    setTimeout(() => setSaveToast(null), 3000);
    load();
    onDataChanged?.();
  };

  const openOverlay = (day) => {
    const kommt = day.data?.erster_stempel ? fmtTime(day.data.erster_stempel) : '';
    const geht = day.data?.letzter_stempel ? fmtTime(day.data.letzter_stempel) : '';
    const mittag_min = parseInt(day.data?.mittag_pausen_min) || 0;
    const pausen_min = parseInt(day.data?.kurze_pausen_min) || 0;
    const soll_min = parseInt(day.data?.soll_minuten) || 480;
    setOverlayDay({
      datum: day.iso,
      bestehendeDaten: { kommt, geht, mittag_min, pausen_min, soll_min },
      hatBestehendeStempel: !!(day.data?.erster_stempel || day.data?.letzter_stempel),
      anzahlSessions: day.data?.anzahl_sessions || 0,
      // v2.360.0 (#822) P1-1: Overlay warnt bei Feiertag/Abwesenheit
      tagesInfo: {
        tag_typ: day.data?.tag_typ || null,
        feiertag_name: day.data?.feiertag_name || null,
        abwesenheit_art_key: day.data?.abwesenheit_art_key || null,
        abwesenheit_label: day.data?.abwesenheit_label || null,
      },
    });
  };

  if (!maId) return null;

  const maName = maInfo
    ? `${maInfo.vorname || ''} ${maInfo.nachname || maInfo.name || ''}`.trim()
    : (zk ? `MA #${zk.mitarbeiter_id}` : `MA #${maId}`);

  // Build day grid
  const monatDate = new Date(viewMonat + 'T00:00:00');
  const daysInMonth = new Date(monatDate.getFullYear(), monatDate.getMonth() + 1, 0).getDate();
  const today = new Date().toISOString().split('T')[0];
  // #871: DB-Wochentag (0=Mo..6=So) → JS-dow (0=So..6=Sa)
  const inaktiveJsDows = new Set(inaktiveWochentage.map(wt => (wt + 1) % 7));
  const allDays = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${viewMonat.substring(0, 8)}${String(d).padStart(2, '0')}`;
    const dt = new Date(iso + 'T00:00:00');
    const dow = dt.getDay();
    const isWeekend = dow === 0 || dow === 6;
    const isMaFreeDay = !isWeekend && inaktiveJsDows.has(dow);
    const tagesRow = tage.find(t => String(t.datum).slice(0, 10) === iso);
    const isFeiertag = !!feiertage[iso];
    const feiertagName = feiertage[iso] || null;
    const isKorrigiert = !!korrekturenByDay[iso];
    allDays.push({ d, iso, dow, isWeekend, isMaFreeDay, isFeiertag, feiertagName, data: tagesRow || null, isKorrigiert });
  }
  // Trend chart
  const trendData = [...history].reverse().map(h => {
    const m = new Date(h.monat);
    return {
      label: m.toLocaleDateString(locale === 'de' ? 'de-AT' : 'en-US', { month: 'short', year: '2-digit' }),
      soll: parseFloat(h.soll_stunden) || 0,
      ist: parseFloat(h.ist_stunden) || 0,
    };
  });

  // Is current month? (for "Laufend" badge)
  const nowMonat = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; })();
  const isCurrentMonth = viewMonat === nowMonat;
  const isPast = viewMonat < nowMonat;

  // #869: Monats-Navigation — min = Eintritts-Monat des MA (falls bekannt), max = aktueller Monat.
  const shiftMonat = (delta) => {
    const d = new Date(viewMonat + 'T00:00:00');
    d.setMonth(d.getMonth() + delta);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  };
  const eintrittMonat = maInfo?.eintritt_datum
    ? `${String(maInfo.eintritt_datum).slice(0, 7)}-01`
    : null;
  const austrittMonat = maInfo?.austritt_datum
    ? `${String(maInfo.austritt_datum).slice(0, 7)}-01`
    : null;
  const maxMonat = austrittMonat && austrittMonat < nowMonat ? austrittMonat : nowMonat;
  const canGoBack = !eintrittMonat || viewMonat > eintrittMonat;
  const canGoForward = viewMonat < maxMonat;
  const goPrev = () => { if (canGoBack) setViewMonat(shiftMonat(-1)); };
  const goNext = () => { if (canGoForward) setViewMonat(shiftMonat(1)); };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/20" />
      <div
        className="relative w-full max-w-2xl bg-[var(--bg-card,#fff)] border-l border-[var(--border-default,#E5E7EB)] h-full overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[var(--bg-card,#fff)] border-b border-[var(--border-card,#E5E7EB)] px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-[var(--text-primary)] truncate">{maName}</h2>
              {zk?.ist_abgeschlossen && (
                <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">
                  <Lock size={10} className="inline mr-0.5" />{t('zeitkonto.abgeschlossen', 'Abgeschlossen')}
                </span>
              )}
              {isCurrentMonth && !zk?.ist_abgeschlossen && (
                <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">
                  {t('zeitkonto.laufend', 'Laufend')}
                </span>
              )}
            </div>
            {/* #869: Monats-Navigation Pfeile links/rechts um Monats-Label */}
            <div className="flex items-center gap-1 mt-1">
              <button
                onClick={goPrev}
                disabled={!canGoBack}
                aria-label={t('zeitkonto.prevMonth', 'Vormonat')}
                className="p-0.5 rounded hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs text-[var(--text-tertiary)] min-w-[110px] text-center">
                {monatDate.toLocaleDateString(locale === 'de' ? 'de-AT' : 'en-US', { month: 'long', year: 'numeric' })}
              </span>
              <button
                onClick={goNext}
                disabled={!canGoForward}
                aria-label={t('zeitkonto.nextMonth', 'Folgemonat')}
                className="p-0.5 rounded hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight size={14} />
              </button>
              {maInfo?.kostenstelle && (
                <span className="text-xs text-[var(--text-tertiary)] ml-2 truncate">· {maInfo.kostenstelle}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ExportDropdown monat={viewMonat} maId={maId} t={t} />
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)]">
              <X size={20} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-center text-[var(--text-tertiary)]">...</div>
        ) : !zk ? (
          <div className="p-6 text-center text-[var(--text-tertiary)]">{t('zeitkonto.noData')}</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-lg px-4 py-3 bg-[var(--bg-card,#fff)]">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">{t('zeitkonto.soll')}</div>
                <div className="text-lg font-bold text-[var(--text-primary)]">{fmtH(anzeige ? anzeige.soll_min / 60 : zk.soll_stunden)}h</div>
                <div className="text-[10px] text-[var(--text-muted)]">{anzeige?.stichtag ? t('zeitkonto.standBis', { defaultValue: 'Stand bis {{datum}}', datum: fmtStichtag(anzeige.stichtag) }) : `${zk.soll_tage} ${t('zeitkonto.arbeitstage')}`}</div>
              </div>
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-lg px-4 py-3 bg-[var(--bg-card,#fff)]">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">{t('zeitkonto.ist')}</div>
                <div className="text-lg font-bold text-[var(--text-primary)]">{fmtH(anzeige ? anzeige.ist_min / 60 : zk.ist_stunden)}h</div>
                <div className="text-[10px] text-[var(--text-muted)]">{zk.ist_tage} {t('zeitkonto.tageGestempelt')}</div>
              </div>
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-lg px-4 py-3 bg-[var(--bg-card,#fff)]">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">{t('zeitkonto.delta')}</div>
                <div className="text-lg font-bold text-[var(--text-primary)]" data-testid="zk-kopf-delta">{fmtDelta(anzeige ? anzeige.delta_min / 60 : zk.delta_stunden)}h</div>
                <div className={`text-[10px] font-medium ${(anzeige ? anzeige.delta_min : parseFloat(zk.delta_stunden)) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {(anzeige ? anzeige.delta_min : parseFloat(zk.delta_stunden)) >= 0 ? t('zeitkonto.plus') : t('zeitkonto.minus')}
                </div>
              </div>
              <div className={`border rounded-lg px-4 py-3 ${parseFloat(zk.za_saldo) >= 200 ? 'border-red-300 bg-red-50' : 'border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)]'}`}>
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]" title={t('zeitkonto.tooltipOffenerBestand', 'Offenes Zeitguthaben seit 01.04.2026: geleistete Mehr-/Überstunden abzüglich genommenem Zeitausgleich und Auszahlungen.')}>{t('zeitkonto.offenerBestand', 'Offener Bestand')}</div>
                <div className={`text-lg font-bold ${parseFloat(zk.za_saldo) >= 200 ? 'text-red-700' : 'text-[var(--text-primary)]'}`}>{fmtH(zk.za_saldo)}h</div>
                <div className="text-[10px] text-[var(--text-muted)]">{t('zeitkonto.vormonat')}: {fmtH(zk.za_saldo_vormonat)}h</div>
                {parseFloat(zk.za_saldo) >= 200 && (
                  <div className="text-[10px] mt-1 text-red-700 font-medium" title={t('zeitkonto.zaSaldoWarnTooltip', 'Hohe ZA-Salden über 200h sind compliance-relevant: Verjährungsrisiko, Auszahlungspflicht bei Austritt, AZG-Grenzen.')}>
                    {t('zeitkonto.zaSaldoWarnung', 'Compliance-Risiko: Saldo über 200h')}
                  </div>
                )}
              </div>
            </div>

            {/* #890: Mehr/Überstunden + 3-Monats-Grace */}
            {mehrUeber && (mehrUeber.mehrstunden.gesamt_min > 0 || mehrUeber.ueberstunden.gesamt_min > 0) && (
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 bg-[var(--bg-card,#fff)]">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]" title={t('zeitkonto.mehrUeberTooltip', 'Real verfügbare Mehr- und Überstunden für Zeitausgleich-Einträge in FIFO-Reihenfolge (mit Grace-Filter).')}>
                    {t('zeitkonto.mehrUeberTitle', 'FIFO-Bestand für ZA-Abzug')}
                  </h3>
                  <div className="flex items-center gap-3">
                    {isAdmin && mehrUeber.ueberstunden.gesamt_min > 0 && (
                      <button
                        type="button"
                        onClick={() => setAuszahlOpen(true)}
                        className="text-[11px] font-medium text-[var(--primary,#338edc)] border border-[var(--primary,#338edc)] rounded-lg px-2.5 py-1 hover:bg-[var(--primary,#338edc)] hover:text-white transition-colors"
                        title={t('zeitkonto.auszahlenTooltip', 'Überstunden auszahlen (reduziert den Bestand 1:1, ohne Faktor). Reine Darstellung, keine Lohnberechnung.')}
                      >
                        {t('zeitkonto.ueberstundenAuszahlen', 'Überstunden auszahlen')}
                      </button>
                    )}
                    <span className="text-[10px] text-[var(--text-muted)]" title={t('zeitkonto.tooltipFaktoren', 'Mehrstunden-Faktor (1,25) gilt nach Ablauf der Grace-Frist (3 Monate). Überstunden-Faktor (1,5) gilt immer — keine Schonfrist.')}>
                      {t('zeitkonto.grace', 'Grace')}: {mehrUeber.faktoren.grace_monate} {t('zeitkonto.monate', 'Monate')}
                      {' · '}{t('zeitkonto.faktorMehr', 'Mehr')} {String(mehrUeber.faktoren.mehrstunden).replace('.', ',')}
                      {' · '}{t('zeitkonto.faktorUeber', 'Über')} {String(mehrUeber.faktoren.ueberstunden).replace('.', ',')}
                    </span>
                  </div>
                </div>
                <div className="text-[11px] text-[var(--text-tertiary)] mb-3 leading-relaxed border-l-2 border-[var(--border-default,#E5E7EB)] pl-3">
                  {t('zeitkonto.legendeMehrstunden', 'Mehrstunden')}: {t('zeitkonto.legendeMehrstundenText', 'Plus-Zeit bis Tages-Vollzeit (8h). Bei Vollzeit-MA nur an Tagen mit reduziertem Soll (Halbtag, Teil-Abwesenheit).')}<br/>
                  {t('zeitkonto.legendeUeberstunden', 'Überstunden')}: {t('zeitkonto.legendeUeberstundenText', 'Plus-Zeit über Tages-Vollzeit hinaus.')}<br/>
                  {t('zeitkonto.legendeGrace', 'Grace')}: {t('zeitkonto.legendeGraceText', '3-Monats-Schonfrist nach Entstehung — Mehrstunden werden 1:1 abgebaut (Faktor 1,0). Danach gilt 1,25.')}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 bg-[var(--bg-inset,#F8F9FC)]">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]" title={t('zeitkonto.tooltipMehrstunden', 'Plus-Zeit bis Tages-Vollzeit (8h)')}>{t('zeitkonto.mehrstunden', 'Mehrstunden')}</div>
                    <div className="text-lg font-bold text-[var(--text-primary)]">{fmtMinToH(mehrUeber.mehrstunden.gesamt_min)}</div>
                    <div className="text-[10px] text-emerald-600" title={t('zeitkonto.tooltipImGrace', 'Innerhalb 3-Monats-Schonfrist — Faktor 1,0 (1:1 Abbau)')}>
                      {fmtMinToH(mehrUeber.mehrstunden.grace_min)} {t('zeitkonto.imGrace', 'im Grace')} (1,0)
                    </div>
                    <div className="text-[10px] text-[var(--text-muted)]" title={t('zeitkonto.tooltipNachGrace', 'Nach Ablauf der 3-Monats-Schonfrist — Faktor 1,25 beim Abbau')}>
                      {fmtMinToH(mehrUeber.mehrstunden.nach_grace_min)} {t('zeitkonto.nachGrace', 'nach Grace')} ({String(mehrUeber.faktoren.mehrstunden).replace('.', ',')})
                    </div>
                  </div>
                  <div className="border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 bg-[var(--bg-inset,#F8F9FC)]">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]" title={t('zeitkonto.tooltipUeberstunden', 'Plus-Zeit über Tages-Vollzeit hinaus')}>{t('zeitkonto.ueberstunden', 'Überstunden')}</div>
                    <div className="text-lg font-bold text-[var(--text-primary)]">{fmtMinToH(mehrUeber.ueberstunden.gesamt_min)}</div>
                    <div className="text-[10px] text-[var(--text-muted)]" title={t('zeitkonto.tooltipImmer', 'Überstunden haben keine Schonfrist — Faktor 1,5 gilt immer beim Abbau')}>
                      {t('zeitkonto.faktor', 'Faktor')} {String(mehrUeber.faktoren.ueberstunden).replace('.', ',')} ({t('zeitkonto.immer', 'immer')})
                    </div>
                  </div>
                  <div className="border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 bg-[var(--bg-inset,#F8F9FC)]">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">{t('zeitkonto.gesamtOffen', 'Gesamt offen')}</div>
                    <div className="text-lg font-bold text-[var(--text-primary)]">
                      {fmtMinToH(mehrUeber.mehrstunden.gesamt_min + mehrUeber.ueberstunden.gesamt_min)}
                    </div>
                    <div className="text-[10px] text-[var(--text-muted)]">
                      {mehrUeber.mehrstunden.log.length + mehrUeber.ueberstunden.log.length} {t('zeitkonto.eintraege', 'Einträge')}
                    </div>
                  </div>
                </div>
                {/* #1567 Phase D: Bilanz Gesamt-Zeitguthaben (brutto = za_genommen + ausgezahlt + offen) */}
                <div className="mt-4 pt-3 border-t border-[var(--border-default,#E5E7EB)]">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-2" title={t('zeitkonto.bilanzTooltip', 'Aufschlüsselung des geleisteten Zeitguthabens: Geleistet minus genommener Zeitausgleich minus Auszahlungen ergibt den offenen Bestand.')}>
                    {t('zeitkonto.bilanzTitle', 'Bilanz Zeitguthaben')}
                  </div>
                  <div className="space-y-1 text-sm max-w-md">
                    <div className="flex items-center justify-between">
                      <span className="text-[var(--text-secondary)]">{t('zeitkonto.bilanzGeleistet', 'Geleistet (brutto)')}</span>
                      <span className="font-semibold text-[var(--text-primary)]">{fmtH((mehrUeber.brutto_min || 0) / 60)} h</span>
                    </div>
                    {mehrUeber.za_genommen_min > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-[var(--text-tertiary)]" title={t('zeitkonto.bilanzZaAbzugTooltip', 'Bewerteter Abzug vom ZA-Konto — kann kleiner sein als die genommene ZA-Zeit (Faktor 1,25 / 1,5).')}>− {t('zeitkonto.bilanzZaAbzug', 'Vom ZA-Konto abgezogen')}</span>
                        <span className="text-[var(--text-secondary)]">{fmtH(mehrUeber.za_genommen_min / 60)} h</span>
                      </div>
                    )}
                    {mehrUeber.ausgezahlt_min > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-[var(--text-tertiary)]" title={t('zeitkonto.ausgezahltTooltip', 'Bereits ausbezahlte Überstunden — 1:1 aus dem Bestand abgezogen (keine Lohnberechnung).')}>− {t('zeitkonto.ausgezahlt', 'Ausgezahlt')}</span>
                        <span className="text-[var(--text-secondary)]">{fmtH(mehrUeber.ausgezahlt_min / 60)} h</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-1 mt-1 border-t border-[var(--border-default,#E5E7EB)]">
                      <span className="font-semibold text-[var(--text-primary)]">= {t('zeitkonto.offenerBestand', 'Offener Bestand')}</span>
                      <span className="text-base font-bold text-[var(--text-primary)]">{fmtH((mehrUeber.offen_gesamt_min || 0) / 60)} h</span>
                    </div>
                  </div>
                </div>
                {(mehrUeber.mehrstunden.log.length > 0 || mehrUeber.ueberstunden.log.length > 0) && (
                  <details className="mt-3">
                    <summary className="text-xs text-[var(--text-tertiary)] cursor-pointer select-none hover:text-[var(--text-secondary)]">
                      {t('zeitkonto.bestandDetails', 'Bestand-Details (FIFO)')}
                    </summary>
                    <div className="mt-2 max-h-48 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] border-b border-[var(--border-table,#F3F4F6)]">
                            <th className="text-left py-1">{t('zeitkonto.entstanden', 'Entstanden')}</th>
                            <th className="text-left py-1">{t('zeitkonto.typ', 'Typ')}</th>
                            <th className="text-right py-1" title={t('zeitkonto.tooltipVerbleibend', 'Noch nicht durch Zeitausgleich abgebaut — offener FIFO-Bestand')}>{t('zeitkonto.verbleibend', 'Verbleibend')}</th>
                            <th className="text-right py-1" title={t('zeitkonto.tooltipFaktor', 'Faktor beim Abbau: Mehrstunden 1,0 im Grace / 1,25 nach Grace, Überstunden 1,5 immer')}>{t('zeitkonto.faktor', 'Faktor')}</th>
                            <th className="text-left py-1 pl-3">{t('zeitkonto.status', 'Status')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            ...mehrUeber.mehrstunden.log.map(l => ({ ...l, typ: 'mehr' })),
                            ...mehrUeber.ueberstunden.log.map(l => ({ ...l, typ: 'ueber' })),
                          ].sort((a, b) => a.entstanden_am.localeCompare(b.entstanden_am) || (a.typ === 'mehr' ? -1 : 1)).map(l => (
                            <tr key={`${l.typ}-${l.id}`} className="border-b border-[var(--border-table,#F3F4F6)]">
                              <td className="py-1 font-mono text-[11px] text-[var(--text-secondary)]">{fmtDate(l.entstanden_am)}</td>
                              <td className="py-1">
                                <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${l.typ === 'mehr' ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700'}`}>
                                  {l.typ === 'mehr' ? t('zeitkonto.mehr', 'Mehr') : t('zeitkonto.ueber', 'Über')}
                                </span>
                              </td>
                              <td className="py-1 text-right font-mono text-[11px]">{fmtMinToH(l.verbleibend_min)}</td>
                              <td className="py-1 text-right font-mono text-[11px]">{String(l.faktor_beim_abbau).replace('.', ',')}</td>
                              <td className="py-1 pl-3 text-[10px] text-[var(--text-muted)]">
                                {l.typ === 'mehr'
                                  ? (l.grace_aktiv
                                      ? `${t('zeitkonto.graceBisZum', 'Grace bis')} ${fmtDate(l.grace_ende)}`
                                      : t('zeitkonto.nachGrace', 'nach Grace'))
                                  : t('zeitkonto.immer', 'immer')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            )}

            {/* Cumulative yearly saldo — Recharts LineChart */}
            {jahressaldo && jahressaldo.data && jahressaldo.data.length > 0 && (() => {
              const chartData = [
                { label: t('zeitkonto.start', 'Start'), kumuliert: jahressaldo.anfangssaldo || 0, isStart: true },
                ...jahressaldo.data.map(m => ({
                  label: new Date(m.monat).toLocaleDateString(locale === 'de' ? 'de-AT' : 'en-US', { month: 'short' }),
                  kumuliert: m.kumuliert,
                  delta: m.delta,
                  monat: m.monat,
                })),
              ];
              const currentMonthIdx = (() => {
                // #869: Marker zeigt den gerade angezeigten Monat (viewMonat), nicht pauschal heute.
                const idx = jahressaldo.data.findIndex(m => String(m.monat).slice(0, 10) === viewMonat);
                return idx >= 0 ? idx + 1 : -1; // +1 because of Start entry
              })();
              return (
                <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 bg-[var(--bg-card,#fff)]">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
                      {t('zeitkonto.saldoVerlauf', 'Saldo-Verlauf')} {jahressaldo.jahr}
                    </h3>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-[var(--text-muted)]">
                        {t('zeitkonto.anfang', 'Anfang')}: <span className="font-medium text-[var(--text-secondary)]">{fmtDelta(jahressaldo.anfangssaldo || 0)}h</span>
                      </span>
                      <span className={`font-bold ${jahressaldo.kumuliert_gesamt >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {t('zeitkonto.aktuell', 'Aktuell')}: {fmtDelta(jahressaldo.kumuliert_gesamt)}h
                      </span>
                    </div>
                  </div>
                  <div style={{ width: '100%', height: 200 }}>
                    <ResponsiveContainer>
                      <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                        <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 11, fill: '#6B7280' }}
                          tickLine={false}
                          axisLine={{ stroke: '#E5E7EB' }}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: '#6B7280' }}
                          tickLine={false}
                          axisLine={{ stroke: '#E5E7EB' }}
                          tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}h`}
                        />
                        <Tooltip
                          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff' }}
                          formatter={(v) => [`${fmtDelta(v)}h`, t('zeitkonto.kumuliertSaldo', 'Kumulierter Saldo')]}
                          labelStyle={{ color: '#1A1A2E', fontWeight: 500 }}
                        />
                        <ReferenceLine y={0} stroke="#9CA3AF" strokeDasharray="4 4" />
                        {currentMonthIdx > 0 && (
                          <ReferenceLine
                            x={chartData[currentMonthIdx]?.label}
                            stroke="#338edc"
                            strokeWidth={1}
                            strokeDasharray="2 2"
                            label={{ value: t('zeitkonto.heute', 'Heute'), position: 'top', fill: '#338edc', fontSize: 10 }}
                          />
                        )}
                        <Line
                          type="monotone"
                          dataKey="kumuliert"
                          stroke="#338edc"
                          strokeWidth={2}
                          dot={{ r: 3, fill: '#338edc', strokeWidth: 0 }}
                          activeDot={{ r: 5, fill: '#338edc', strokeWidth: 2, stroke: '#fff' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })()}

            {/* #1776 Kontoauszug ZA-Konto — Beginn + Veränderung − Abzug = Ende */}
            {kontoauszug && (
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl bg-[var(--bg-card,#fff)] p-4 mb-6">
                <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">{t('zeitkonto.kontoauszugTitle', 'Zeitausgleichs-Konto')}</h3>
                <div className="space-y-1 text-sm max-w-md">
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-secondary)]">{t('zeitkonto.saldoBeginn', 'Saldo Monatsbeginn')}</span>
                    <span className="font-mono text-[var(--text-primary)]">{fmtMinToH(Math.round(kontoauszug.za_saldo_beginn * 60))}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-secondary)]">+ {t('zeitkonto.veraenderungArbeitszeit', 'Veränderung durch Arbeitszeit')}</span>
                    <span className="font-mono text-[var(--text-primary)]">{fmtMinToH(Math.round(kontoauszug.veraenderung_arbeitszeit * 60))}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-secondary)]">− {t('zeitkonto.abzugZeitausgleich', 'Abzug durch Zeitausgleich')}</span>
                    <span className="font-mono text-[var(--text-primary)]">{fmtMinToH(-Math.round(Math.abs(kontoauszug.za_abzug_summe) * 60))}</span>
                  </div>
                  {kontoauszug.ausgezahlt > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-[var(--text-secondary)]">− {t('zeitkonto.ausgezahlt', 'Ausgezahlt')}</span>
                      <span className="font-mono text-[var(--text-primary)]">{fmtMinToH(-Math.round(kontoauszug.ausgezahlt * 60))}</span>
                    </div>
                  )}
                  {kontoauszug.korrekturen !== 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-[var(--text-secondary)]">{t('zeitkonto.korrekturen', 'Korrekturen')}</span>
                      <span className="font-mono text-[var(--text-primary)]">{fmtMinToH(Math.round(kontoauszug.korrekturen * 60))}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1 mt-1 border-t border-[var(--border-default,#E5E7EB)]">
                    <span className="font-semibold text-[var(--text-primary)]">= {t('zeitkonto.saldoEnde', 'Saldo Monatsende')}</span>
                    <span className="text-base font-bold font-mono text-[var(--text-primary)]">{fmtMinToH(Math.round(kontoauszug.za_saldo_ende * 60))}</span>
                  </div>
                </div>
                <div className="text-[11px] text-[var(--text-tertiary)] mt-2">
                  {t('zeitkonto.kontoauszugHinweis', 'Der Saldo Monatsende ist der Saldo Monatsbeginn des Folgemonats.')}
                </div>
              </div>
            )}

            {/* Daily overview table */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('zeitkonto.tagesuebersicht')}</h3>
                {isAdmin && (
                  <span className="text-[10px] text-[var(--text-muted)]">
                    <Pencil size={10} className="inline mr-0.5" />{t('zeitkonto.klickZumBearbeiten', 'Klick auf ✏️ für manuelle Tageseingabe')}
                  </span>
                )}
              </div>
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden bg-[var(--bg-card,#fff)]">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[var(--bg-inset,#F8F9FC)] border-b border-[var(--border-table,#F3F4F6)]">
                        <th className="text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-3 py-2.5">{t('zeitkonto.datum')}</th>
                        <th className="text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-3 py-2.5">{t('zeitkonto.kommt', 'Kommt')}</th>
                        <th className="text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-3 py-2.5">{t('zeitkonto.geht', 'Geht')}</th>
                        <th className="text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-2 py-2.5">{t('zeitkonto.pause', 'Pause')}</th>
                        <th className="text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-2 py-2.5">{t('zeitkonto.mittag', 'Mittag')}</th>
                        <th className="text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-3 py-2.5">{t('zeitkonto.soll')}</th>
                        <th className="text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-3 py-2.5">{t('zeitkonto.netto', 'Netto')}</th>
                        <th className="text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-3 py-2.5">{t('zeitkonto.delta')}</th>
                        <th className="text-right text-[10px] uppercase tracking-wider font-medium text-[var(--text-secondary)] px-2 py-2.5">ZA</th>
                        <th className="text-right text-[10px] uppercase tracking-wider font-medium text-[var(--text-secondary)] px-2 py-2.5" title={t('zeitkonto.zaKontoAbzug', 'ZA-Konto Abzug')}>{t('zeitkonto.zaAbzug', 'ZA-Abzug')}</th>
                        <th className="text-right text-[10px] uppercase tracking-wider font-medium text-[var(--text-secondary)] px-2 py-2.5" title={t('zeitkonto.mehrstunden', 'Mehrstunden')}>{t('zeitkonto.mehr', 'Mehr')}</th>
                        <th className="text-right text-[10px] uppercase tracking-wider font-medium text-[var(--text-secondary)] px-2 py-2.5" title={t('zeitkonto.ueberstunden', 'Überstunden')}>{t('zeitkonto.ueber', 'Über')}</th>
                        {isAdmin && <th className="w-10 px-2 py-2.5"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {allDays.map(day => {
                        const isToday = day.iso === today;
                        const isFuture = day.iso > today;
                        const tagTyp = day.data?.tag_typ || null;
                        const abwKey = day.data?.abwesenheit_art_key || null;
                        const abwLabel = day.data?.abwesenheit_label || null;
                        const feiertagLabel = day.feiertagName || day.data?.feiertag_name || null;

                        // Row coloring: tag_typ > isFeiertag > isWeekend > default
                        let rowClass = '';
                        if (tagTyp === 'laufend') {
                          rowClass = 'bg-blue-50 dark:bg-blue-900/10';
                        } else if (tagTyp === 'feiertag' || day.isFeiertag) {
                          rowClass = 'bg-amber-50 dark:bg-amber-900/10 text-[var(--text-muted)]';
                        } else if (tagTyp === 'abwesenheit_bezahlt') {
                          if (abwKey === 'krankenstand') rowClass = 'bg-red-50 dark:bg-red-900/10 text-[var(--text-secondary)]';
                          else if (abwKey === 'urlaub') rowClass = 'bg-blue-50 dark:bg-blue-900/10 text-[var(--text-secondary)]';
                          else if (abwKey === 'zeitausgleich') rowClass = 'bg-indigo-50 dark:bg-indigo-900/10 text-[var(--text-secondary)]';
                          else rowClass = 'bg-emerald-50 dark:bg-emerald-900/10 text-[var(--text-secondary)]';
                        } else if (tagTyp === 'abwesenheit_unbezahlt') {
                          rowClass = 'bg-gray-50 dark:bg-gray-900/10 text-[var(--text-muted)]';
                        } else if (day.isWeekend || day.isMaFreeDay) {
                          rowClass = 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-muted)]';
                        } else if (isFuture) {
                          rowClass = 'text-[var(--text-muted)]';
                        } else if (isToday) {
                          rowClass = 'border-l-2 border-l-[#338edc] bg-[var(--primary-ghost,rgba(51,142,220,0.08))]';
                        }

                        // #1776: an ZA-Tagen zeigt Netto die ECHTE Arbeitszeit statt des auf SOLL
                        //   aufgefüllten Werts; delta_minuten bleibt in der DB unverändert.
                        const zaMin = day.data ? (parseInt(day.data.za_min, 10) || 0) : 0;
                        const zaAbzugMin = day.data ? (parseInt(day.data.za_abzug_min, 10) || 0) : 0;
                        const nettoAnzeigeMin = day.data
                          ? (zaMin > 0 ? (parseInt(day.data.arbeit_min, 10) || 0) : day.data.netto_minuten)
                          : null;
                        // #1864 F5: Delta = persistiertes Tages-Delta (wie ZA-Saldo/Toepfe/Lohnbericht),
                        //   nicht mehr `arbeit − soll` — das las sich an ZA-Tagen als Doppelbelastung.
                        const delta = day.data ? (parseFloat(day.data.delta_minuten) || 0) : 0;
                        const isNonWorkday = tagTyp === 'feiertag' || tagTyp === 'abwesenheit_bezahlt' || tagTyp === 'abwesenheit_unbezahlt' || day.isFeiertag || day.isMaFreeDay;
                        const noDataYet = !day.data && !day.isWeekend && !day.isMaFreeDay && !day.isFeiertag && !isFuture;
                        const canEdit = isAdmin && !day.isWeekend && !isNonWorkday && !isFuture;
                        const dayKorr = korrekturenByDay[day.iso] || [];

                        // Label-Chip Farbe je Abwesenheitstyp
                        const labelChipClass =
                          tagTyp === 'feiertag' ? 'text-amber-600'
                          : tagTyp === 'abwesenheit_bezahlt' && abwKey === 'krankenstand' ? 'text-red-600'
                          : tagTyp === 'abwesenheit_bezahlt' && abwKey === 'urlaub' ? 'text-blue-600'
                          : tagTyp === 'abwesenheit_bezahlt' && abwKey === 'zeitausgleich' ? 'text-indigo-600'
                          : tagTyp === 'abwesenheit_bezahlt' ? 'text-emerald-600'
                          : tagTyp === 'abwesenheit_unbezahlt' ? 'text-gray-500'
                          : tagTyp === 'teilabwesenheit' ? 'text-violet-600'
                          : 'text-amber-600';
                        // #1845: An Teil-Abwesenheitstagen traegt der Chip die gutgeschriebene
                        //   Dauer, damit im Zeitkonto sichtbar ist, woher das Netto stammt.
                        //   Der Wert kommt aus tages_arbeitszeit, exakt wie im PDF-Nachweis.
                        const teilabwMin = day.data ? (parseInt(day.data.teilabwesenheit_min, 10) || 0) : 0;
                        // #1864: Traegt der Tag mehrere Abwesenheitsarten, liefert der
                        //   Server den fertigen Text (`abwesenheit_text`) — die Zeile
                        //   baut ihn NICHT nach. Sonst stuende hier "+5:00 Zeitausgleich"
                        //   fuer einen Tag, der zu 5 h Arztbesuch war: die Gutschrift
                        //   stammt von der einen, das Label von der anderen Art.
                        const abwText = day.data?.abwesenheit_text || null;
                        const abwWarnungen = day.data?.abwesenheit_warnungen || [];
                        const chipLabel = feiertagLabel
                          || abwText
                          || (abwLabel && teilabwMin > 0 ? `+${fmtMinToH(teilabwMin)} ${abwLabel}` : abwLabel)
                          || null;
                        // v2.365.1 (Teil C): Tooltip für bezahlte Nicht-Arbeitstage
                        const chipTooltip = abwWarnungen.length
                          ? abwWarnungen.join('; ')
                          : (isNonWorkday && chipLabel
                            ? `${chipLabel} — ${t('zeitkonto.tooltip.paid_day', 'Bezahlter Tag. SOLL wird als erfüllt gewertet, daher Delta 0.')}`
                            : null);
                        const anzahlSessions = day.data?.anzahl_sessions || 0;
                        const hasMultiSessions = anzahlSessions >= 2;
                        const isExpanded = expandedDays.has(day.iso);
                        const sessionsList = hasMultiSessions && Array.isArray(day.data?.stempel_paare)
                          ? (() => {
                              const map = {};
                              for (const p of day.data.stempel_paare) {
                                const idx = p.session ?? 0;
                                if (!map[idx]) map[idx] = { idx, start: null, ende: null, netto: 0, kurze: 0, mittag: 0 };
                                if (p.typ === 'arbeit') {
                                  if (!map[idx].start || new Date(p.ein) < new Date(map[idx].start)) map[idx].start = p.ein;
                                  if (!map[idx].ende || new Date(p.aus) > new Date(map[idx].ende)) map[idx].ende = p.aus;
                                  map[idx].netto += p.dauer_min || 0;
                                } else if (p.typ === 'kurze_pause') {
                                  map[idx].kurze += p.dauer_min || 0;
                                } else if (p.typ === 'mittagspause') {
                                  map[idx].mittag += p.dauer_min || 0;
                                }
                              }
                              return Object.values(map).sort((a, b) => a.idx - b.idx);
                            })()
                          : [];
                        return (
                          <React.Fragment key={day.d}>
                          <tr className={`border-b border-[var(--border-table,#F3F4F6)] ${rowClass}`}>
                            <td className="px-3 py-1.5 font-mono text-xs">
                              <span className="text-[var(--text-tertiary)] w-6 inline-block">{dayName(day.iso, locale)}</span>{' '}
                              {fmtDate(day.iso)}
                              {chipLabel && <span title={chipTooltip || undefined} className={`ml-1 text-[9px] font-normal ${labelChipClass} ${chipTooltip ? 'cursor-help underline decoration-dotted' : ''}`}>{chipLabel}</span>}
                              {/* #1864: Eine Ueberschreitung des Tagessolls oder eine zweite
                                  ganztaegige Art wird GEMELDET, nicht still gekappt. */}
                              {abwWarnungen.length > 0 && (
                                <span title={abwWarnungen.join('; ')} className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 cursor-help">
                                  {t('zeitkonto.abwPruefen', 'prüfen')}
                                </span>
                              )}
                              {hasMultiSessions && (
                                <button
                                  onClick={() => toggleExpand(day.iso)}
                                  className="ml-1 inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-[#338edc] bg-opacity-10 text-[#338edc] hover:bg-opacity-20 transition-colors cursor-pointer"
                                  title={t('zeitkonto.sessionsExpand', 'Sessions ein-/ausblenden')}
                                >
                                  {anzahlSessions} {t('zeitkonto.sessions', 'Sessions')}
                                  {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                                </button>
                              )}
                              {dayKorr.length > 0 && (
                                <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700">
                                  {t('zeitkonto.korrigiert', 'corrected')}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono text-xs text-[var(--text-secondary)]">
                              {day.data?.erster_stempel ? fmtTime(day.data.erster_stempel) : '—'}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono text-xs text-[var(--text-secondary)]">
                              {day.data?.letzter_stempel ? fmtTime(day.data.letzter_stempel) : '—'}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-xs text-[var(--text-tertiary)]">
                              <span className="inline-flex items-center gap-1 justify-end">
                                {day.data?.zu_viele_kurzpausen && (
                                  <span
                                    title={day.data?.pausen_warnung || t('mittagspause.kurzpausenKontingent', 'Kurzpausen-Tageskontingent überschritten')}
                                    className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold cursor-help"
                                  >
                                    !
                                  </span>
                                )}
                                {day.data && parseInt(day.data.kurze_pausen_min) > 0 ? `${parseInt(day.data.kurze_pausen_min)}m` : ''}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-xs text-[var(--text-tertiary)]">
                              {(() => {
                                const abw = Array.isArray(day.data?.abweichungen) ? day.data.abweichungen : [];
                                const abgezogen  = abw.find(a => a.code === 'PFLICHT_MITTAG_ABGEZOGEN');
                                const aufgerundet = abw.find(a => a.code === 'PFLICHT_MITTAG_AUFGERUNDET');
                                const split       = abw.find(a => a.code === 'MITTAG_SPLIT');
                                const ueber       = abw.find(a => a.code === 'MITTAG_UEBERMAESSIG');
                                let badge = null;
                                if (ueber)        badge = { cls: 'bg-red-100 text-red-700',      title: ueber.text,       label: '!' };
                                else if (split)   badge = { cls: 'bg-orange-100 text-orange-700', title: split.text,       label: '↯' };
                                else if (abgezogen)  badge = { cls: 'bg-amber-100 text-amber-700', title: abgezogen.text,  label: '−' };
                                else if (aufgerundet) badge = { cls: 'bg-amber-100 text-amber-700', title: aufgerundet.text, label: '↑' };
                                return (
                                  <span className="inline-flex items-center gap-1 justify-end">
                                    {badge && (
                                      <span title={badge.title} className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold cursor-help ${badge.cls}`}>
                                        {badge.label}
                                      </span>
                                    )}
                                    {day.data && parseInt(day.data.mittag_pausen_min) > 0 ? `${parseInt(day.data.mittag_pausen_min)}m` : (abgezogen ? '0m' : '')}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono text-xs">
                              {day.isWeekend || day.isMaFreeDay ? '—' : day.data ? fmtMinToH(day.data.soll_minuten) : (isFuture ? '—' : fmtMinToH(parseFloat(zk.soll_stunden) / Math.max(zk.soll_tage, 1) * 60))}
                            </td>
                            <td className={`px-3 py-1.5 text-right font-mono text-xs ${noDataYet ? 'text-[var(--text-muted)]' : ''}`}>
                              {tagTyp === 'laufend' ? (
                                <span className="text-[#338edc] italic">{t('zeitkonto.laufend', 'Laufend')}</span>
                              ) : nettoAnzeigeMin != null ? fmtMinToH(nettoAnzeigeMin) : '—'}
                            </td>
                            <td className={`px-3 py-1.5 text-right font-mono text-xs font-medium ${
                              noDataYet ? 'text-[var(--text-muted)]' :
                              isNonWorkday || tagTyp === 'laufend' ? 'text-[var(--text-muted)]' :
                              tagTyp === 'offen' && delta < 0 ? '' :
                              day.data ? (delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-500' : '') : ''
                            }`}>
                              {/* #1845: Der Wert stand hier hinter einem „?" — das PDF druckte
                                  denselben Tag mit seiner Zahl, die beiden Ansichten widersprachen
                                  sich also. Jetzt zeigen beide die Zahl; der offene Tag bleibt
                                  orange markiert und fuer Admins weiterhin anklickbar. */}
                              {tagTyp === 'offen' && delta < 0 ? (
                                isAdmin && canEdit ? (
                                  <button
                                    onClick={() => openOverlay(day)}
                                    title={t('zeitkonto.tagOffenTooltip', 'Kein Stempel an einem Arbeitstag. Klicken zum Erfassen.')}
                                    className="inline-flex items-center justify-center px-1.5 h-5 rounded bg-orange-100 text-orange-700 text-[11px] font-bold cursor-pointer hover:bg-orange-200 transition-colors"
                                  >
                                    {fmtMinToH(delta)}
                                  </button>
                                ) : (
                                  <span
                                    title={t('zeitkonto.tagOffenTooltip', 'Kein Stempel an einem Arbeitstag.')}
                                    className="inline-flex items-center justify-center px-1.5 h-5 rounded bg-orange-100 text-orange-700 text-[11px] font-bold cursor-help"
                                  >
                                    {fmtMinToH(delta)}
                                  </span>
                                )
                              ) : tagTyp === 'laufend' || !day.data || day.data.delta_minuten == null ? '—' : fmtMinToH(delta)}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-[11px]">
                              {zaMin > 0 ? fmtMinToH(zaMin) : <span className="text-[var(--text-muted)]">—</span>}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-[11px]">
                              {zaAbzugMin > 0 ? fmtMinToH(-zaAbzugMin) : <span className="text-[var(--text-muted)]">—</span>}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-[11px] text-[var(--text-tertiary)]">
                              {day.data && parseInt(day.data.mehrstunden_min) > 0 ? fmtMinToH(day.data.mehrstunden_min) : ''}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-[11px] text-[var(--text-tertiary)]">
                              {day.data && parseInt(day.data.ueberstunden_min) > 0 ? fmtMinToH(day.data.ueberstunden_min) : ''}
                            </td>
                            {isAdmin && (
                              <td className="px-2 py-1.5 text-center">
                                {canEdit && (
                                  <button
                                    onClick={() => openOverlay(day)}
                                    className="p-1 rounded hover:bg-[var(--primary-ghost,rgba(51,142,220,0.08))] text-[var(--text-tertiary)] hover:text-[#338edc] transition-colors"
                                    title={t('zeitkonto.manuelleEingabeTitle', 'Manuelle Tageseingabe')}
                                  >
                                    <Pencil size={12} />
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                          {/* #819: Session-Details (aufklappbar) */}
                          {hasMultiSessions && isExpanded && sessionsList.length > 0 && (
                            <tr className="border-b border-[var(--border-table,#F3F4F6)]">
                              <td colSpan={isAdmin ? 13 : 12} className="px-3 py-2 bg-[var(--bg-inset,#F8F9FC)]">
                                <div className="flex flex-col gap-1">
                                  {sessionsList.map(sess => (
                                    <div key={sess.idx} className="flex items-center gap-3 text-[11px] font-mono text-[var(--text-secondary)]">
                                      <span className="text-[#338edc] font-semibold w-16">
                                        {t('zeitkonto.session', 'Session')} {sess.idx + 1}
                                      </span>
                                      <span>{sess.start ? fmtTime(sess.start) : '—'} → {sess.ende ? fmtTime(sess.ende) : <span className="italic text-[#338edc]">{t('zeitkonto.laufend', 'Laufend')}</span>}</span>
                                      <span className="text-[var(--text-tertiary)]">
                                        {t('zeitkonto.netto', 'Netto')}: {fmtMinToH(sess.netto)}
                                      </span>
                                      {sess.kurze > 0 && (
                                        <span className="text-[var(--text-tertiary)]">
                                          {t('zeitkonto.kurzePausen', 'Kurze Pausen')}: {sess.kurze}m
                                        </span>
                                      )}
                                      {sess.mittag > 0 && (
                                        <span className="text-[var(--text-tertiary)]">
                                          {t('zeitkonto.mittag', 'Mittag')}: {sess.mittag}m
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                          {/* Korrektur-History under day */}
                          {dayKorr.length > 0 && (
                            <tr className="border-b border-[var(--border-table,#F3F4F6)]">
                              <td colSpan={isAdmin ? 13 : 12} className="px-3 py-1 bg-amber-50/50 dark:bg-amber-900/5">
                                <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                                  {dayKorr.map(k => {
                                    // Localize field name (new format: kommt/geht, old format: stempel/zeitpunkt)
                                    const feldLabel = k.feld === 'kommt' || k.aktion === 'add' && k.neuer_wert?.includes('arbeit_start')
                                      ? t('zeitkonto.kommt', 'Kommt')
                                      : k.feld === 'geht' || k.aktion === 'add' && k.neuer_wert?.includes('arbeitsende')
                                      ? t('zeitkonto.geht', 'Geht')
                                      : k.feld === 'zeitpunkt' ? t('zeitkonto.zeit', 'Zeit') : k.feld;
                                    // Clean neuer_wert: strip raw typ names from old format
                                    const cleanVal = (v) => {
                                      if (!v) return '—';
                                      return v.replace(/arbeit_start\s*@\s*/i, '').replace(/arbeitsende\s*@\s*/i, '').replace(/pause_ende\s*@\s*/i, '').trim() || v;
                                    };
                                    return (
                                      <span key={k.id} className="text-[10px] text-[var(--text-tertiary)]">
                                        {k.korr_vorname} {k.korr_nachname}: {feldLabel} {cleanVal(k.alter_wert)} → {cleanVal(k.neuer_wert)}
                                        {k.grund_text && <span className="italic ml-1">({k.grund_text})</span>}
                                      </span>
                                    );
                                  })}
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Monthly trend chart */}
            {trendData.length >= 2 && (
              <div>
                <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">{t('zeitkonto.monatsTrend')}</h3>
                <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 bg-[var(--bg-card,#fff)]">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={trendData} barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-table,#F3F4F6)" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} unit="h" />
                      <Tooltip
                        contentStyle={{ background: 'var(--bg-card,#fff)', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 8, fontSize: 12 }}
                        formatter={(v) => [`${v.toFixed(1)}h`]}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="soll" name={t('zeitkonto.soll')} fill="#E5E7EB" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="ist" name={t('zeitkonto.ist')} fill="#338edc" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}

        {saveToast && (
          <div className="fixed bottom-6 right-6 z-[60] px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium shadow-sm flex items-center gap-2">
            <Check size={16} /> {saveToast}
          </div>
        )}

      </div>
      {overlayDay && (
        <ManuelleEingabeOverlay
          mitarbeiterId={maId}
          mitarbeiterName={maName}
          datum={overlayDay.datum}
          bestehendeDaten={overlayDay.bestehendeDaten}
          hatBestehendeStempel={overlayDay.hatBestehendeStempel}
          anzahlSessions={overlayDay.anzahlSessions}
          tagesInfo={overlayDay.tagesInfo}
          onClose={() => setOverlayDay(null)}
          onSaved={handleOverlaySaved}
          t={t}
          locale={locale}
        />
      )}
      {auszahlOpen && (
        <UeberstundenAuszahlenDialog
          maId={maId}
          maName={maName}
          ueberBestandMin={mehrUeber?.ueberstunden?.gesamt_min || 0}
          onClose={() => setAuszahlOpen(false)}
          onSaved={handleAuszahlSaved}
          t={t}
          locale={locale}
        />
      )}
    </div>
  );
}

// ─── #1558 Überstunden-Auszahlung-Dialog (HR-Eingabe, bestand-bezogen) ───
function UeberstundenAuszahlenDialog({ maId, maName, ueberBestandMin, onClose, onSaved, t, locale }) {
  const heute = new Date().toISOString().slice(0, 10);
  const [stunden, setStunden] = useState('');
  const [datum, setDatum] = useState(heute);
  const [notiz, setNotiz] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const minuten = Math.round((parseFloat(String(stunden).replace(',', '.')) || 0) * 60);
  const bestandH = (ueberBestandMin / 60).toFixed(2).replace('.', ',');
  const datumOk = datum >= '2026-04-01';
  const minutenOk = minuten > 0;
  const bestandOk = minuten <= ueberBestandMin;
  const canSave = datumOk && minutenOk && bestandOk && !saving;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await apiPost('/api/zeitkonto/ueberstunden-auszahlung', {
        mitarbeiter_id: maId, datum, minuten, notiz: notiz.trim() || null,
      });
      onSaved();
    } catch (e) {
      setError(e?.error || t('zeitkonto.auszahlError', 'Speichern fehlgeschlagen'));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-default,#E5E7EB)] w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-[var(--text-primary)]">{t('zeitkonto.ueberstundenAuszahlen', 'Überstunden auszahlen')}</h3>
          <button type="button" onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]"><X size={18} /></button>
        </div>
        <p className="text-[12px] text-[var(--text-tertiary)] mb-4 leading-relaxed">
          {maName ? `${maName} · ` : ''}{t('zeitkonto.auszahlBestand', 'Verfügbarer Überstunden-Bestand')}: <span className="font-mono font-semibold text-[var(--text-secondary)]">{bestandH} h</span>
          <br />{t('zeitkonto.auszahlHinweis', 'Reduziert den Bestand 1:1 (ohne Faktor). Reine Darstellung, keine Lohnberechnung.')}
        </p>
        <div className="space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-[var(--text-tertiary)]">{t('zeitkonto.stunden', 'Stunden')}</span>
            <input type="number" min="0" step="0.25" value={stunden} onChange={e => setStunden(e.target.value)}
              className="mt-1 w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm" placeholder="z.B. 2 oder 2,5" />
            {minuten > 0 && <span className="text-[10px] text-[var(--text-muted)]">= {minuten} {t('zeitkonto.minuten', 'Minuten')}</span>}
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-[var(--text-tertiary)]">{t('zeitkonto.datum', 'Datum')}</span>
            <input type="date" min="2026-04-01" value={datum} onChange={e => setDatum(e.target.value)}
              className="mt-1 w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm" />
            {!datumOk && <span className="text-[10px] text-red-600">{t('zeitkonto.auszahlDatumFehler', 'Datum muss am oder nach dem 01.04.2026 liegen.')}</span>}
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-[var(--text-tertiary)]">{t('zeitkonto.notiz', 'Notiz')} ({t('zeitkonto.optional', 'optional')})</span>
            <input type="text" value={notiz} onChange={e => setNotiz(e.target.value)} maxLength={1000}
              className="mt-1 w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm" />
          </label>
          {minuten > 0 && !bestandOk && (
            <div className="text-[11px] text-red-600">{t('zeitkonto.auszahlBestandFehler', 'Mehr als der verfügbare Bestand')} ({bestandH} h).</div>
          )}
          {error && <div className="text-[11px] text-red-600">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="text-sm px-3 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">{t('common.cancel', 'Abbrechen')}</button>
          <button type="button" onClick={submit} disabled={!canSave}
            className="text-sm px-3 py-2 rounded-lg bg-[var(--primary,#338edc)] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? t('common.saving', 'Speichert…') : t('zeitkonto.auszahlen', 'Auszahlen')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───
export default function ZeitkontoPage() {
  const { t, locale } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [monat, setMonat] = useState(() => {
    const monatParam = searchParams.get('monat');
    if (monatParam && /^\d{4}-\d{2}(-\d{2})?$/.test(monatParam)) {
      return monatParam.length === 7 ? `${monatParam}-01` : monatParam;
    }
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [data, setData] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ col: 'name', dir: 'asc' });
  const [selectedMa, setSelectedMa] = useState(() => {
    const maParam = searchParams.get('ma');
    return maParam ? parseInt(maParam) : null;
  });
  const [recalculating, setRecalculating] = useState(false);
  const [showFormer, setShowFormer] = useState(false);
  const [activeTab, setActiveTab] = useState('uebersicht');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAbt, setFilterAbt] = useState('');

  const isAdmin = ['administrator', 'admin', 'ops_manager', 'teamleiter'].includes(user?.rolle);

  const monatLabel = useMemo(() => {
    const d = new Date(monat + 'T00:00:00');
    return d.toLocaleDateString(locale === 'de' ? 'de-AT' : 'en-US', { month: 'long', year: 'numeric' });
  }, [monat, locale]);

  // Is current month?
  const nowMonat = useMemo(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; }, []);
  const isCurrentMonth = monat === nowMonat;

  const load = useCallback(() => {
    setLoading(true);
    const formerParam = showFormer ? '&include_former=true' : '';
    Promise.all([
      api(`/api/zeitkonto?monat=${monat}${formerParam}`),
      api(`/api/zeitkonto/statistiken?monat=${monat}`),
    ]).then(([list, st]) => {
      setData(list.data || []);
      setStats(st.data || null);
    }).finally(() => setLoading(false));
  }, [monat, showFormer]);

  useEffect(() => { load(); }, [load]);

  const prevMonat = () => {
    const d = new Date(monat + 'T00:00:00');
    d.setMonth(d.getMonth() - 1);
    setMonat(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
  };
  const nextMonat = () => {
    const d = new Date(monat + 'T00:00:00');
    d.setMonth(d.getMonth() + 1);
    setMonat(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await apiPost('/api/zeitkonto/berechnen', { monat });
      load();
    } finally {
      setRecalculating(false);
    }
  };

  // Extract unique departments for filter
  const departments = useMemo(() => {
    const set = new Set();
    data.forEach(r => { if (r.kostenstelle) set.add(r.kostenstelle); else if (r.department_name) set.add(r.department_name); });
    return [...set].sort();
  }, [data]);

  // Sorting + filtering
  const sorted = useMemo(() => {
    let list = [...data];

    // Filter by search
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(r => (r.name || '').toLowerCase().includes(q) || (r.vorname || '').toLowerCase().includes(q) || (r.nachname || '').toLowerCase().includes(q));
    }

    // Filter by department
    if (filterAbt) {
      list = list.filter(r => (r.kostenstelle || r.department_name || '') === filterAbt);
    }

    const { col, dir } = sort;
    list.sort((a, b) => {
      let va = a[col], vb = b[col];
      if (['soll_stunden', 'ist_stunden', 'delta_stunden', 'ueberstunden', 'minusstunden', 'za_saldo'].includes(col)) {
        va = parseFloat(va) || 0; vb = parseFloat(vb) || 0;
      }
      if (typeof va === 'string') return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return dir === 'asc' ? va - vb : vb - va;
    });
    return list;
  }, [data, sort, searchTerm, filterAbt]);

  const teamPct = stats && parseFloat(stats.avg_soll) > 0
    ? Math.round((parseFloat(stats.avg_ist) / parseFloat(stats.avg_soll)) * 100)
    : 0;

  // Overtime warnings
  const warnUe = useMemo(() => {
    const max = 20; // from system_config default
    return parseFloat(stats?.sum_ueberstunden || 0) / Math.max(parseInt(stats?.ma_count || 1), 1) > max ? 'Hoch' : null;
  }, [stats]);

  return (
    <div className={`p-page transition-[padding] duration-200 ${selectedMa ? 'xl:pr-[696px]' : ''}`}>
      {/* Header + Tab Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-purple-50">
              <Clock size={20} className="text-[#8B5CF6]" />
            </div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">{t('zeitkonto.title')}</h1>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)]">
              {[
                { key: 'uebersicht', label: t('zeitkonto.tabUebersicht', 'Übersicht') },
                { key: 'einstellungen', label: t('modulSettings.title', 'Einstellungen') },
              ].map(tb => (
                <button key={tb.key} onClick={() => setActiveTab(tb.key)}
                  className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${
                    activeTab === tb.key
                      ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]'
                      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                  }`}>{tb.label}</button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Month navigation */}
          <div className="flex items-center gap-1 bg-[var(--bg-card)] border border-[var(--border-default,#E5E7EB)] rounded-lg px-1 py-1">
            <button onClick={prevMonat} className="p-1.5 rounded hover:bg-[var(--bg-inset)] text-[var(--text-secondary)]">
              <ChevronLeft size={16} />
            </button>
            <span className="px-3 text-sm font-medium text-[var(--text-primary)] min-w-[140px] text-center">
              {monatLabel}
            </span>
            <button onClick={nextMonat} className="p-1.5 rounded hover:bg-[var(--bg-inset)] text-[var(--text-secondary)]">
              <ChevronRight size={16} />
            </button>
          </div>
          <label className="flex items-center gap-2 px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg bg-[var(--bg-card)] cursor-pointer select-none">
            <input type="checkbox" checked={showFormer} onChange={e => setShowFormer(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-[var(--border-default)]" />
            <span className="text-xs text-[var(--text-secondary)]">{t('mitarbeiter.zeigeEhemalige')}</span>
          </label>
          <ExportDropdown monat={monat} t={t} />
          {isAdmin && (
            <button
              onClick={handleRecalculate}
              disabled={recalculating}
              className="px-3 py-2 text-xs font-medium rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:border-[#338edc] hover:text-[#338edc] transition-colors disabled:opacity-50"
            >
              {recalculating ? '...' : t('zeitkonto.neuBerechnen')}
            </button>
          )}
        </div>
      </div>

      {activeTab === 'uebersicht' && <>
      {/* KPI Bar */}
      {stats && (
        <div className={`grid grid-cols-2 gap-4 mb-5 ${selectedMa ? 'xl:grid-cols-4' : 'lg:grid-cols-4'}`}>
          <Kpi
            icon={Clock} iconColor="text-[#22D3EE]"
            label={t('zeitkonto.avgUeberstunden')}
            value={`${fmtH(parseFloat(stats.sum_ueberstunden) / Math.max(parseInt(stats.ma_count), 1))}h`}
            sub={`${parseInt(stats.ma_plus)} MA ${t('zeitkonto.mitPlus')}`}
            warning={warnUe}
          />
          <Kpi
            icon={Clock} iconColor="text-[#22D3EE]"
            label={t('zeitkonto.zaSaldo')}
            value={`${fmtH(parseFloat(stats.avg_za_saldo))}h`}
            sub={`${t('zeitkonto.durchschnitt', 'Ø')} ${t('zeitkonto.proMa')}`}
          />
          <Kpi
            icon={TrendingUp} iconColor="text-[#10B981]"
            label={t('zeitkonto.teamAuslastung')}
            value={`${teamPct}%`}
            sub={`${fmtH(stats.avg_ist)}h / ${fmtH(stats.avg_soll)}h`}
          />
          <Kpi
            icon={Users} iconColor="text-[#8B5CF6]"
            label={t('zeitkonto.maMitMinus')}
            value={`${parseInt(stats.ma_minus)} MA`}
            sub={`${fmtH(stats.sum_minusstunden)}h ${t('zeitkonto.gesamt')}`}
          />
        </div>
      )}

      {/* Filter bar */}
      {data.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
            <input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-8 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20"
              placeholder={t('zeitkonto.suchen', 'Suchen...')}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
                <X size={14} />
              </button>
            )}
          </div>
          {departments.length > 1 && (
            <select
              value={filterAbt}
              onChange={e => setFilterAbt(e.target.value)}
              className="px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]"
            >
              <option value="">{t('zeitkonto.alleAbteilungen', 'Alle Abteilungen')}</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          <span className="text-xs text-[var(--text-muted)]">
            {sorted.length} {t('zeitkonto.vonTotal', 'von')} {data.length} MA
          </span>
        </div>
      )}

      {/* No data info */}
      {!loading && data.length === 0 && (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-8 text-center">
          <Clock size={32} className="mx-auto mb-3 text-[var(--text-muted)]" />
          <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1">{t('zeitkonto.noData')}</h3>
          <p className="text-sm text-[var(--text-tertiary)]">{t('zeitkonto.noDataDesc')}</p>
          {isAdmin && (
            <button onClick={handleRecalculate} className="mt-4 px-4 py-2 text-sm font-medium rounded-lg bg-[#338edc] text-white hover:bg-[#2b7bc4]">
              {t('zeitkonto.neuBerechnen')}
            </button>
          )}
        </div>
      )}

      {/* Team Table */}
      {sorted.length > 0 && (
        <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--bg-inset,#F8F9FC)] border-b border-[var(--border-table,#F3F4F6)]">
                  <SortHeader col="name" sort={sort} setSort={setSort}>{t('zeitkonto.name')}</SortHeader>
                  <SortHeader col="soll_stunden" sort={sort} setSort={setSort} align="right">{t('zeitkonto.soll')}</SortHeader>
                  <SortHeader col="ist_stunden" sort={sort} setSort={setSort} align="right">{t('zeitkonto.ist')}</SortHeader>
                  <SortHeader col="delta_stunden" sort={sort} setSort={setSort} align="right">{t('zeitkonto.delta')}</SortHeader>
                  <SortHeader col="ueberstunden" sort={sort} setSort={setSort} align="right">{t('zeitkonto.ueberstunden')}</SortHeader>
                  <SortHeader col="za_saldo" sort={sort} setSort={setSort} align="right">{t('zeitkonto.zaSaldo')}</SortHeader>
                  <th className="text-center text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] px-4 py-3 w-16">
                    {t('zeitkonto.status', 'Status')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
                {sorted.map(row => {
                  const delta = parseFloat(row.delta_stunden) || 0;
                  const zaSaldo = parseFloat(row.za_saldo) || 0;
                  const ue = parseFloat(row.ueberstunden) || 0;
                  const ist = parseFloat(row.ist_stunden) || 0;
                  const isFormer = row.ist_aktiv === false;
                  // Future month or IST=0 in current month → dimmed, not red
                  const noWorkYet = isCurrentMonth && ist === 0 && delta < 0;
                  return (
                    <tr
                      key={row.id}
                      className={`hover:bg-[var(--bg-inset,#F8F9FC)] cursor-pointer transition-colors ${isFormer ? 'opacity-50' : ''}`}
                      onClick={() => setSelectedMa(row.mitarbeiter_id)}
                    >
                      <td className="px-4 py-3">
                        <MitarbeiterListItem
                          mitarbeiter={{ ...row, id: row.mitarbeiter_id, name: row.name || `${row.vorname || ''} ${row.nachname || ''}`.trim() }}
                          size="sm"
                          trailing={isFormer ? (
                            <span className="text-[10px] font-normal text-[var(--text-muted)]">({t('mitarbeiter.ehemalig')})</span>
                          ) : null}
                        />
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-[var(--text-secondary)]">
                        {fmtH(row.soll_stunden)}h
                      </td>
                      <td className={`px-4 py-3 text-right font-mono ${noWorkYet ? 'text-[var(--text-muted)]' : 'text-[var(--text-secondary)]'}`}>
                        {fmtH(row.ist_stunden)}h
                      </td>
                      <td className={`px-4 py-3 text-right font-mono font-medium ${
                        noWorkYet ? 'text-[var(--text-muted)]' :
                        delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-500' : 'text-[var(--text-secondary)]'
                      }`}>
                        {fmtDelta(row.delta_stunden)}h
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-[var(--text-secondary)]">
                        {ue > 0 ? (
                          <span className="inline-flex items-center gap-1">
                            {fmtH(row.ueberstunden)}h
                            {ue > 20 && <AlertTriangle size={12} className="text-amber-500" />}
                          </span>
                        ) : '—'}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono ${zaSaldo > 10 ? 'font-bold' : ''} text-[var(--text-secondary)]`}>
                        {fmtH(row.za_saldo)}h
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {row.ist_abgeschlossen && (
                            <span className="px-1.5 py-0.5 text-[9px] font-medium rounded-full bg-emerald-100 text-emerald-700">
                              <Lock size={9} className="inline" />
                            </span>
                          )}
                          <ArrowRight size={14} className="text-[var(--text-muted)]" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Panel */}
      {selectedMa && (
        <DetailPanel
          maId={selectedMa}
          monat={monat}
          onClose={() => setSelectedMa(null)}
          onDataChanged={load}
          t={t}
          locale={locale}
          isAdmin={isAdmin}
        />
      )}

      </>}

      {activeTab === 'einstellungen' && (
        <ModulEinstellungen kategorien={['zeitkonto']} />
      )}
    </div>
  );
}
